import { useState, useEffect } from 'react';
import { getAdminSystemConfigs, createAdminSystemConfig, updateAdminSystemConfig } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const EMPTY = { name: '', key: '', value: '' };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const AdminSystemConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | config object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const isMobile = useIsMobile();

  const load = () => {
    setLoading(true);
    getAdminSystemConfigs().then((res) => setConfigs(res.data.configs)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setMsg(''); setModal('add'); };
  const openEdit = (c) => { setForm({ name: c.name, key: c.key, value: c.value }); setMsg(''); setModal(c); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      if (modal === 'add') {
        await createAdminSystemConfig(form);
      } else {
        await updateAdminSystemConfig(modal._id, form);
      }
      load();
      setModal(null);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 2 }}>System Config</h4>
          <div style={{ color: '#555', fontSize: 13 }}>{configs.length} configs stored</div>
        </div>
        <button onClick={openAdd} style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Add Config
        </button>
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['#', 'Name', 'Key', 'Value', 'Updated', 'Actions'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 16px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : configs.length === 0 ? (
              <tr><td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No configs yet</td></tr>
            ) : configs.map((c, i) => (
              <tr key={c._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ color: '#555', fontSize: 12, padding: '12px 16px' }}>{i + 1}</td>
                <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '12px 16px' }}>{c.name}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: 'rgba(0,229,255,0.1)', color: cyan, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>{c.key}</span>
                </td>
                <td style={{ color: '#888', fontSize: 13, padding: '12px 16px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.value}</td>
                <td style={{ color: '#666', fontSize: 12, padding: '12px 16px' }}>{fmtIST(c.updatedAt)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => openEdit(c)} style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', color: cyan, borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 380 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>{modal === 'add' ? 'Add Config' : 'Edit Config'}</div>
            <form onSubmit={handleSave}>
              {[
                { label: 'Name', key: 'name', type: 'text', placeholder: 'e.g. Withdrawal Fee Percent' },
                { label: 'Key', key: 'key', type: 'text', placeholder: 'e.g. withdrawal_fee_percent' },
                { label: 'Value', key: 'value', type: 'text', placeholder: 'e.g. 2.5' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input type={type} placeholder={placeholder} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    disabled={key === 'key' && modal !== 'add'}
                    style={{ width: '100%', background: key === 'key' && modal !== 'add' ? 'rgba(255,255,255,0.03)' : 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: key === 'key' && modal !== 'add' ? '#666' : '#fff', fontSize: 14, outline: 'none' }}
                    required />
                </div>
              ))}
              {msg && <div style={{ color: '#ff6666', fontSize: 13, marginBottom: 14 }}>{msg}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setModal(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: '10px', background: saving ? 'rgba(255,255,255,0.06)' : cyan, border: 'none', color: saving ? '#555' : '#000', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSystemConfig;
